import axios from "./axiosInstance";
import config from "./config";

/**
 * Event add-ons and time slots (PM 4e4384e4). Every event can sell extras
 * (photo packages, a meet shirt) and can ask families to book a time slot.
 * Prices are always the server's: a registration names add-ons by id only
 * (backend services/eventAddons).
 */

const BASE_URL = config.BASE_URL;

const headers = (token: string | null) => ({
  "Content-Type": "application/json",
  authorization: `Bearer ${token}`,
});

export interface EventAddon {
  id: string;
  event_id?: string;
  label: string;
  description: string | null;
  amount: number;
  sort_order?: number;
  status?: "active" | "archived";
}

/** A slot a family can book. Never carries a capacity or a count. */
export interface EventTimeSlot {
  id: string;
  name: string;
  start_at: string | null;
  end_at: string | null;
  location: string | null;
}

export interface RegistrationOptions {
  addon_selection: "any" | "one";
  addon_required: boolean;
  session_signup: boolean;
  addons: EventAddon[];
  time_slots: EventTimeSlot[];
}

export interface RosterEntry {
  registration_id: string;
  family_name: string;
  family_phone: string | null;
  athlete_name: string | null;
  options: { label: string; quantity: number }[];
  price_option: string | null;
  notes: string | null;
  status: string;
  payment_status: string | null;
}

export interface RosterSlot {
  id: string;
  name: string;
  start_at: string | null;
  end_at: string | null;
  location: string | null;
  capacity: number | null;
  registrations: RosterEntry[];
}

export interface SlotRoster {
  event: { id: string; name: string; dates: any; location: string | null; session_signup: boolean };
  slots: RosterSlot[];
  unassigned: RosterEntry[];
}

/** The rules and choices an event's registration form shows. Throws on failure. */
export const getRegistrationOptions = async (token: string | null, eventId: string): Promise<RegistrationOptions> => {
  const r = await axios.get(`${BASE_URL}/events/${eventId}/registration-options`, { headers: headers(token) });
  return r.data.data;
};

/** The same, for the public sign-up page (no account). Throws on failure. */
export const getPublicRegistrationOptions = async (tenantId: string, eventId: string): Promise<RegistrationOptions> => {
  const r = await axios.get(`${BASE_URL}/public/events/${eventId}/registration-options`, {
    headers: { "x-tenant-id": tenantId },
  });
  return r.data.data;
};

export const listAddons = async (token: string | null, eventId: string): Promise<EventAddon[]> => {
  const r = await axios.get(`${BASE_URL}/events/${eventId}/addons`, { headers: headers(token) });
  return r.data.items || [];
};

export const createAddon = async (
  token: string | null,
  eventId: string,
  body: { label: string; amount: number; description?: string | null; sort_order?: number }
): Promise<EventAddon> => {
  const r = await axios.post(`${BASE_URL}/events/${eventId}/addons`, body, { headers: headers(token) });
  return r.data.item;
};

export const updateAddon = async (
  token: string | null,
  eventId: string,
  addonId: string,
  body: Partial<Pick<EventAddon, "label" | "amount" | "description" | "sort_order" | "status">>
): Promise<EventAddon> => {
  const r = await axios.patch(`${BASE_URL}/events/${eventId}/addons/${addonId}`, body, { headers: headers(token) });
  return r.data.item;
};

export const deleteAddon = async (token: string | null, eventId: string, addonId: string): Promise<void> => {
  await axios.delete(`${BASE_URL}/events/${eventId}/addons/${addonId}`, { headers: headers(token) });
};

/** The event's add-on rules: pick one or any, required, families book a time slot. */
export const saveAddonRules = async (
  token: string | null,
  eventId: string,
  rules: { addon_selection: "any" | "one"; addon_required: boolean; session_signup: boolean }
) => {
  const r = await axios.put(`${BASE_URL}/events/${eventId}`, rules, { headers: headers(token) });
  return r.data;
};

/** Staff: who is in each time slot and what they bought. Throws on failure. */
export const getSlotRoster = async (token: string | null, eventId: string): Promise<SlotRoster> => {
  const r = await axios.get(`${BASE_URL}/events/${eventId}/slot-roster`, { headers: headers(token) });
  return r.data.data;
};

/** Sum of the picked add-ons at the listed prices, in cents-safe dollars (display only). */
export const addonsTotal = (addons: EventAddon[], pickedIds: string[]): number => {
  const cents = addons
    .filter((a) => pickedIds.includes(a.id))
    .reduce((sum, a) => sum + Math.round(Number(a.amount) * 100), 0);
  return cents / 100;
};
